import * as THREE from 'three';
import { SURFACE_REST_POSITION_ATTRIBUTE } from '../../../materials/core/SurfaceCoordinates';
import { hash01 } from './hash';
import { CLAY_DEEP, CLAY_SALMON, MORTAR, MORTAR_CRUST, SOOT, STAIN_GREY, TERRACOTTA } from './palette';

export interface BrickMaterialHandles {
  material: THREE.MeshStandardMaterial;
  setDebug(on: boolean): void;
  setAtlas(tex: THREE.Texture): void;
  setResidue(tex: THREE.Texture, originX: number, originY: number, sizeX: number, sizeY: number): void;
  setMortar(x: number, y: number, z: number): void;
  setCrownBoost(v: number): void;
  dispose(): void;
}

export interface DebrisMaterialHandles {
  material: THREE.MeshStandardMaterial;
  dispose(): void;
}

/**
 * Band-limited value noise + octave fade. Mined from unused
 * `src/materials/BrickMaterial.ts` / SurfaceNoise / TriplanarProjection:
 * unfaded octaves shimmer; mix toward 0.5 with fwidth fade, clamp height
 * derivatives and fold the discarded gradient into roughness (Toksvig-lite).
 */
const BRICK_NOISE_GLSL = /* glsl */ `
float gBrickRough;
float gBrickAo;
float gBrickHeight;
float gSpecAa;

float brickHash11(float n) {
  n = fract(n * 0.1031);
  n *= n + 33.33;
  n *= n + n;
  return fract(n);
}
float brickHash21(vec2 p, float s) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031 + s * 0.131);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
float brickValueNoise(vec2 p, float s) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);
  float a = brickHash21(i, s);
  float b = brickHash21(i + vec2(1.0, 0.0), s);
  float c = brickHash21(i + vec2(0.0, 1.0), s);
  float d = brickHash21(i + vec2(1.0, 1.0), s);
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
float brickOctaveFade(float wavelength, float fp) {
  return clamp(wavelength / max(fp * 4.0, 1.0e-6) - 0.7, 0.0, 1.0);
}
float brickNoiseBL(vec2 uv, float freq, float s, float fp) {
  float wavelength = 1.0 / max(freq, 1.0);
  float fade = brickOctaveFade(wavelength, fp);
  return mix(0.5, brickValueNoise(uv * freq, s), fade);
}
`;

const VERT_ATTR = /* glsl */ `
attribute vec4 instanceA;
attribute vec4 instanceB;
attribute vec4 instanceC;
attribute vec4 instanceD;
attribute vec4 instanceE;
attribute vec4 instanceF;
uniform vec2 uWallMin;
uniform vec2 uWallSize;
flat varying float vState;
flat varying float vSeed;
flat varying float vVariant;
flat varying float vRot;
flat varying float vCorner;
flat varying float vEdge;
flat varying float vPocks;
flat varying float vThrough;
flat varying vec4 vPock01;
flat varying vec2 vPock2;
flat varying vec2 vAng;
varying vec3 vLocal;
flat varying float vFilm;
flat varying float vScorch;
flat varying float vCrackVis;
flat varying float vMirror;
flat varying float vBackSpall;
flat varying float vBackScorch;
varying vec2 vWallUv;
`;

const VERT_BODY = /* glsl */ `
vState = instanceA.x;
vSeed = instanceA.y;
vVariant = instanceA.z;
vRot = instanceA.w;
vCorner = instanceB.x;
vEdge = instanceB.y;
vPocks = instanceB.z;
vThrough = instanceB.w;
vPock01 = instanceC;
vPock2 = instanceD.xy;
vAng = instanceD.zw;
vLocal = position;
vFilm = instanceE.x;
vScorch = instanceE.y;
vCrackVis = instanceE.z;
vMirror = instanceE.w;
vBackSpall = instanceF.x;
vBackScorch = instanceF.y;
vec4 wp = instanceMatrix * vec4(position, 1.0);
vWallUv = (wp.xy - uWallMin) / max(uWallSize, vec2(1.0e-4));
`;

const FRAG_UNIFORMS = /* glsl */ `
uniform sampler2D uAtlas;
uniform sampler2D uResidue;
uniform float uDebug;
uniform vec3 uHalf;
uniform vec3 uMortar;
uniform vec2 uWallMin;
uniform vec2 uWallSize;
uniform float uCrownBoost;
flat varying float vState;
flat varying float vSeed;
flat varying float vVariant;
flat varying float vRot;
flat varying float vCorner;
flat varying float vEdge;
flat varying float vPocks;
flat varying float vThrough;
flat varying vec4 vPock01;
flat varying vec2 vPock2;
flat varying vec2 vAng;
varying vec3 vLocal;
flat varying float vFilm;
flat varying float vScorch;
flat varying float vCrackVis;
flat varying float vMirror;
flat varying float vBackSpall;
flat varying float vBackScorch;
varying vec2 vWallUv;
${BRICK_NOISE_GLSL}
`;

const FRAG_BODY = /* glsl */ `
if (vState > 3.5) discard;

gBrickRough = 0.86;
gBrickAo = 1.0;
gBrickHeight = 0.5;
gSpecAa = 0.0;

vec3 albedo = diffuseColor.rgb;
float halfL = uHalf.x;
float halfH = uHalf.y;
float halfD = uHalf.z;
vec2 faceUv = vec2(vLocal.x / (halfL * 2.0) + 0.5, vLocal.y / (halfH * 2.0) + 0.5);
float front = step(halfD - 0.003, vLocal.z);
float backFace = step(halfD - 0.003, -vLocal.z);

float edge = min(min(faceUv.x, 1.0 - faceUv.x), min(faceUv.y, 1.0 - faceUv.y));
albedo *= mix(0.82, 1.0, smoothstep(0.0, 0.07, edge));

vec3 an = abs(vLocal) / max(uHalf, vec3(1.0e-5));
float ex = 1.0 - an.x;
float ey = 1.0 - an.y;
float ez = 1.0 - an.z;
float edge3 = min(ex, ey);
if (an.x >= an.y && an.x >= an.z) edge3 = min(ey, ez);
else if (an.y >= an.z) edge3 = min(ex, ez);

float fwUv = fwidth(faceUv.x) + fwidth(faceUv.y);
vec3 tw = pow(max(an, vec3(1.0e-4)), vec3(8.0));
tw /= (tw.x + tw.y + tw.z);
vec2 uvXY = faceUv;
vec2 uvZY = vec2(vLocal.z / (halfD * 2.0) + 0.5, faceUv.y);
vec2 uvXZ = vec2(faceUv.x, vLocal.z / (halfD * 2.0) + 0.5);
float fwSurf = fwUv;

float s0 = vSeed;
float s1 = fract(vSeed * 7.132 + 0.173);

vec2 surfUv = uvXY;
if (tw.x > tw.y && tw.x > tw.z) surfUv = uvZY;
else if (tw.y > tw.z) surfUv = uvXZ;
float nBroad = brickNoiseBL(surfUv, 3.1, s0 * 19.0, fwSurf);
float grain = brickNoiseBL(surfUv, 5.4, s1 * 11.0, fwSurf);
float cuFade = brickOctaveFade(0.48, fwSurf);
vec2 cu = uvXY * 2.0 - 1.0;
float bowl = (1.0 - dot(cu, cu)) * (s0 - 0.38);
float diag = sin(uvXY.x * 2.15 + s1 * 6.28318) * sin(uvXY.y * 1.72 + s0 * 4.188);

vec2 ru = faceUv - 0.5;
if (vMirror > 0.5) ru.x = -ru.x;
if (vMirror > 1.5) ru.y = -ru.y;
float ang = vRot * 3.14159265 / 180.0;
float cs = cos(ang);
float sn = sin(ang);
vec2 rotUv = vec2(ru.x * cs - ru.y * sn, ru.x * sn + ru.y * cs) + 0.5;
float col = mod(vVariant, 4.0);
float row = floor(mod(vVariant, 16.0) * 0.25);
vec2 tile = vec2(col, row) * 0.25;
vec2 auv = rotUv * 0.25 + tile;
auv = clamp(auv, tile + 0.002, tile + 0.248);
vec4 mask = texture2D(uAtlas, auv);

vec3 clay = vec3(${CLAY_SALMON[0]}, ${CLAY_SALMON[1]}, ${CLAY_SALMON[2]});
vec3 clayDeep = vec3(${CLAY_DEEP[0]}, ${CLAY_DEEP[1]}, ${CLAY_DEEP[2]});
vec3 chip = vec3(0.16, 0.08, 0.055);
vec3 crackCol = vec3(0.1, 0.055, 0.04);
vec3 terracotta = vec3(${TERRACOTTA[0]}, ${TERRACOTTA[1]}, ${TERRACOTTA[2]});
vec3 stainCol = vec3(${STAIN_GREY[0]}, ${STAIN_GREY[1]}, ${STAIN_GREY[2]});

vec2 hairOrigin = vec2(0.38 + fract(vSeed * 1.73) * 0.24, 0.36 + fract(vSeed * 2.19) * 0.28);
vec2 rel0 = faceUv - hairOrigin;
vec2 rel1 = faceUv - (hairOrigin + vec2(0.07, -0.05));
float hd0 = abs(rel0.x * sin(vAng.x) - rel0.y * cos(vAng.x));
float hd1 = abs(rel0.x * sin(vAng.y) - rel0.y * cos(vAng.y));
float hd2 = abs(rel1.x * sin(vAng.x + 0.62) - rel1.y * cos(vAng.x + 0.62));
float fw0 = max(fwidth(hd0), 1.0e-5);
float fw1 = max(fwidth(hd1), 1.0e-5);
float fw2 = max(fwidth(hd2), 1.0e-5);
float w0 = max(0.010, fw0 * 1.15);
float w1 = max(0.0085, fw1 * 1.15);
float w2 = max(0.0075, fw2 * 1.15);
float fade0 = 1.0 - smoothstep(0.028, 0.070, fw0);
float fade1 = 1.0 - smoothstep(0.028, 0.070, fw1);
float fade2 = 1.0 - smoothstep(0.028, 0.070, fw2);
float h0 = (1.0 - smoothstep(w0 * 0.22, w0, hd0)) * fade0;
float h1 = (1.0 - smoothstep(w1 * 0.22, w1, hd1)) * fade1;
float h2 = (1.0 - smoothstep(w2 * 0.22, w2, hd2)) * fade2;
float hairFade = 1.0 - smoothstep(0.045, 0.12, fwUv);
float hairs = max(h0, max(h1, h2));
hairs = max(hairs, mask.b * hairFade);
hairs *= hairFade * front;

float nearClay = smoothstep(0.16, 0.58, clamp(vCrackVis, 0.0, 1.0));

if (vState > 2.5) {
  float sp = mask.r * front;
  float inner = smoothstep(0.38, 0.72, sp);
  float rim = smoothstep(0.10, 0.34, sp) * (1.0 - smoothstep(0.48, 0.82, sp));
  vec3 clayBody = mix(clayDeep, clay, 0.45 + grain * 0.28);
  albedo = mix(albedo, clayBody, inner);
  albedo = mix(albedo, clayDeep * 0.58, rim * 0.92);
  albedo = mix(albedo, chip, mask.g * 0.88 * front);
  albedo = mix(albedo, crackCol, hairs * mix(0.12, 0.45, clamp(vCrackVis, 0.0, 1.0)));
} else if (vState > 1.5) {
  float vis = mix(0.45, 0.95, clamp(vCrackVis, 0.0, 1.0));
  albedo = mix(albedo, crackCol, hairs * vis);
  float jointShade = (1.0 - smoothstep(0.0, 0.055, edge)) * vEdge * vCrackVis;
  albedo = mix(albedo, crackCol, jointShade * front * 0.55);
} else if (vState > 0.5) {
  float sp = mask.r * front * nearClay;
  float inner = smoothstep(0.38, 0.72, sp);
  float rim = smoothstep(0.10, 0.34, sp) * (1.0 - smoothstep(0.48, 0.82, sp));
  vec3 clayBody = mix(clayDeep, clay, 0.4 + grain * 0.3);
  albedo = mix(albedo, clayBody, inner * 0.92);
  albedo = mix(albedo, clayDeep * 0.55, rim * 0.88);
  albedo = mix(albedo, crackCol, hairs * (1.0 - nearClay) * 0.7);
}

if (vState > 2.5) {
  float jointShade = (1.0 - smoothstep(0.0, 0.055, edge)) * vEdge * vCrackVis;
  albedo = mix(albedo, crackCol, jointShade * front * 0.8);
}

float pockAmt = 0.0;
if (vPocks > 0.5) pockAmt = max(pockAmt, smoothstep(0.18, 0.04, distance(faceUv, vPock01.xy)));
if (vPocks > 1.5) pockAmt = max(pockAmt, smoothstep(0.16, 0.038, distance(faceUv, vPock01.zw)));
if (vPocks > 2.5) pockAmt = max(pockAmt, smoothstep(0.16, 0.038, distance(faceUv, vPock2)));
albedo = mix(albedo, vec3(0.1, 0.05, 0.035), pockAmt * front);

if (vThrough > 0.5 && front > 0.5) {
  vec2 origin = vPocks > 0.5 ? vPock01.xy : vec2(0.5);
  vec2 trel = faceUv - origin;
  float td0 = abs(trel.x * sin(vAng.x) - trel.y * cos(vAng.x));
  float td1 = abs(trel.x * sin(vAng.y) - trel.y * cos(vAng.y));
  float tf0 = max(fwidth(td0), 1.0e-5);
  float tf1 = max(fwidth(td1), 1.0e-5);
  float tw0 = max(0.010, tf0 * 1.15);
  float tw1 = max(0.0088, tf1 * 1.15);
  float t0 = (1.0 - smoothstep(tw0 * 0.22, tw0, td0)) * (1.0 - smoothstep(0.028, 0.070, tf0));
  float t1 = (1.0 - smoothstep(tw1 * 0.22, tw1, td1)) * (1.0 - smoothstep(0.028, 0.070, tf1));
  albedo = mix(albedo, crackCol, max(t0, t1) * 0.92 * hairFade);
}

if (backFace > 0.5 && vBackSpall > 0.04) {
  // Ledger backSpall is scaled ~0.52, which sat below the atlas inner/rim
  // thresholds — remap so the mirrored spall pattern actually reads.
  float spAmt = smoothstep(0.05, 0.40, vBackSpall);
  float sp = mask.r * mix(0.42, 1.0, spAmt);
  float inner = smoothstep(0.28, 0.66, sp);
  float rim = smoothstep(0.10, 0.32, sp) * (1.0 - smoothstep(0.48, 0.82, sp));
  vec3 clayBody = mix(clayDeep, clay, 0.38 + grain * 0.22);
  albedo = mix(albedo, clayBody, inner * 0.88);
  albedo = mix(albedo, clayDeep * 0.45, rim * 0.85);
}

float mortarAmt = 0.0;
if (uMortar.x + uMortar.y + uMortar.z > 1.0e-6) {
  vec3 ap = abs(vLocal);
  float bandX = uMortar.x > 1.0e-6 ? (1.0 - smoothstep(halfL - uMortar.x, halfL - uMortar.x * 0.22, ap.x)) : 0.0;
  float bandY = uMortar.y > 1.0e-6 ? (1.0 - smoothstep(halfH - uMortar.y, halfH - uMortar.y * 0.22, ap.y)) : 0.0;
  float bandZ = uMortar.z > 1.0e-6 ? (1.0 - smoothstep(halfD - uMortar.z, halfD - uMortar.z * 0.22, ap.z)) : 0.0;
  if (an.x >= an.y && an.x >= an.z) mortarAmt = max(bandY, bandZ);
  else if (an.y >= an.z) mortarAmt = max(bandX, bandZ);
  else mortarAmt = max(bandX, bandY);
  vec3 mortarCol = mix(
    vec3(${MORTAR[0]}, ${MORTAR[1]}, ${MORTAR[2]}),
    vec3(${MORTAR_CRUST[0]}, ${MORTAR_CRUST[1]}, ${MORTAR_CRUST[2]}),
    grain
  );
  albedo = mix(albedo, mortarCol, clamp(mortarAmt, 0.0, 1.0));
}

float wearFade = brickOctaveFade(0.045, fwSurf);
float wear = (1.0 - smoothstep(0.0, 0.048, edge3)) * wearFade * (1.0 - clamp(mortarAmt, 0.0, 1.0));
albedo = mix(albedo, albedo * vec3(1.13, 1.08, 1.04), wear * 0.5);

vec4 residue = texture2D(uResidue, clamp(vWallUv, vec2(0.0), vec2(1.0)));
float luma = dot(albedo, vec3(0.2126, 0.7152, 0.0722));
vec3 dustTint = mix(vec3(luma), terracotta, 0.24);
float filmAmt = max(residue.r, vFilm) * (0.72 + 0.28 * grain);
albedo = mix(albedo, dustTint, clamp(filmAmt, 0.0, 1.0) * 0.4 * (front + backFace * 0.35));
albedo = mix(albedo, stainCol, residue.g * 0.78 * front);
float scFront = max(residue.b, vScorch) * front;
float scBack = max(vBackScorch * 1.85, residue.b * 0.68) * backFace;
float sc = max(scFront, scBack);
albedo = mix(albedo, vec3(luma) * 0.2, sc * mix(0.72, 0.84, backFace));
albedo *= 1.0 - sc * mix(0.5, 0.62, backFace);

float crater = 0.0;
if (vState > 0.5) crater = mask.r * (front + backFace * 0.55);
float jointAo = mix(0.64, 1.0, smoothstep(0.0, 0.11, edge3));
float groundAo = mix(0.74, 1.0, smoothstep(0.0, 0.20, vWallUv.y));
float craterAo = 1.0 - crater * 0.36 - pockAmt * front * 0.22;
gBrickAo = clamp(jointAo * groundAo * craterAo, 0.48, 1.0);
albedo *= mix(1.0, gBrickAo, 0.38);

float edgeLip = (1.0 - smoothstep(0.0, 0.055, edge3)) * brickOctaveFade(0.05, fwSurf);
float craterFade = brickOctaveFade(0.10, fwSurf);
gBrickHeight = 0.5
  + bowl * 0.034 * cuFade
  + diag * 0.018 * cuFade
  + (nBroad - 0.5) * 0.022
  - crater * 0.042 * craterFade
  - pockAmt * front * 0.028 * craterFade
  - edgeLip * 0.014;

float brickR = mix(0.78, 0.90, s0);
float rough = brickR + (grain - 0.5) * 0.07 + (nBroad - 0.5) * 0.04;
if (vState > 0.5) rough += mix(0.03, 0.09, clamp(vCrackVis + crater, 0.0, 1.0));
rough += wear * 0.07;
rough -= clamp(filmAmt, 0.0, 1.0) * 0.10;
gBrickRough = clamp(rough, 0.75, 0.98);

if (uDebug > 0.5) {
  if (vState < 0.5) albedo = vec3(0.55, 0.22, 0.16);
  else if (vState < 1.5) albedo = vec3(0.86, 0.48, 0.16);
  else if (vState < 2.5) albedo = vec3(0.92, 0.78, 0.22);
  else if (vState < 3.5) albedo = vec3(0.78, 0.28, 0.82);
  else albedo = vec3(0.06, 0.05, 0.05);
}

albedo *= 1.0 + (grain - 0.5) * 0.08;
albedo *= 1.0 + uCrownBoost * smoothstep(0.88, 0.995, vWallUv.y);

diffuseColor.rgb = albedo;
`;

const ROUGH_INJECT = /* glsl */ `
roughnessFactor = gBrickRough;
`;

const NORMAL_INJECT = /* glsl */ `
{
  float dhdx = dFdx(gBrickHeight);
  float dhdy = dFdy(gBrickHeight);
  float rawMag = length(vec2(dhdx, dhdy));
  dhdx = clamp(dhdx, -0.016, 0.016);
  dhdy = clamp(dhdy, -0.016, 0.016);
  gSpecAa += saturate((rawMag - length(vec2(dhdx, dhdy))) * 3.0);
  vec3 sp = -vViewPosition;
  vec3 vN = normalize(normal);
  vec3 sX = dFdx(sp);
  vec3 sY = dFdy(sp);
  vec3 r1 = cross(sY, vN);
  vec3 r2 = cross(vN, sX);
  float det = dot(sX, r1) * faceDirection;
  vec3 grad = sign(det) * (dhdx * r1 + dhdy * r2);
  float bump = 0.30 * brickOctaveFade(0.08, length(fwidth(-vViewPosition)));
  normal = normalize(abs(det) * vN - grad * bump);
  roughnessFactor = clamp(roughnessFactor + gSpecAa, 0.75, 0.98);
}
`;

const AO_INJECT = /* glsl */ `
reflectedLight.indirectDiffuse *= gBrickAo;
`;

export function createBrickMaterial(
  atlas: THREE.Texture,
  half: { x: number; y: number; z: number },
): BrickMaterialHandles {
  const material = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    roughness: 0.86,
    metalness: 0.02,
    envMapIntensity: 0.15,
    dithering: false,
  });
  const uniforms = {
    uAtlas: { value: atlas },
    uResidue: { value: atlas },
    uDebug: { value: 0 },
    uHalf: { value: new THREE.Vector3(half.x, half.y, half.z) },
    uMortar: { value: new THREE.Vector3(0, 0, 0) },
    uWallMin: { value: new THREE.Vector2(-2, 0) },
    uWallSize: { value: new THREE.Vector2(4, 2.7) },
    uCrownBoost: { value: 0 },
  };
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uAtlas = uniforms.uAtlas;
    shader.uniforms.uResidue = uniforms.uResidue;
    shader.uniforms.uDebug = uniforms.uDebug;
    shader.uniforms.uHalf = uniforms.uHalf;
    shader.uniforms.uMortar = uniforms.uMortar;
    shader.uniforms.uWallMin = uniforms.uWallMin;
    shader.uniforms.uWallSize = uniforms.uWallSize;
    shader.uniforms.uCrownBoost = uniforms.uCrownBoost;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${VERT_ATTR}`)
      .replace('#include <uv_vertex>', `#include <uv_vertex>\n${VERT_BODY}`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${FRAG_UNIFORMS}`)
      .replace('vec4 diffuseColor = vec4( diffuse, opacity );', `vec4 diffuseColor = vec4( diffuse, opacity );\n${FRAG_BODY}`)
      .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>\n${ROUGH_INJECT}`)
      .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>\n${NORMAL_INJECT}`)
      .replace('#include <aomap_fragment>', `#include <aomap_fragment>\n${AO_INJECT}`);
  };
  material.customProgramCacheKey = () => 'damage-brick-p4-crown-boost';

  return {
    material,
    setDebug(on: boolean) {
      uniforms.uDebug.value = on ? 1 : 0;
    },
    setAtlas(tex: THREE.Texture) {
      uniforms.uAtlas.value = tex;
    },
    setResidue(tex, originX, originY, sizeX, sizeY) {
      uniforms.uResidue.value = tex;
      uniforms.uWallMin.value.set(originX, originY);
      uniforms.uWallSize.value.set(sizeX, sizeY);
    },
    setMortar(x, y, z) {
      uniforms.uMortar.value.set(x, y, z);
    },
    setCrownBoost(v: number) {
      uniforms.uCrownBoost.value = v;
    },
    dispose() {
      material.dispose();
    },
  };
}

const DEBRIS_VERT = /* glsl */ `
attribute vec4 instanceA;
varying vec3 vObjP;
flat varying float vSeed;
flat varying float vSoot;
flat varying float vBroken;
`;

const DEBRIS_FRAG = /* glsl */ `
varying vec3 vObjP;
flat varying float vSeed;
flat varying float vSoot;
flat varying float vBroken;
${BRICK_NOISE_GLSL}
`;

const DEBRIS_BODY = /* glsl */ `
gBrickRough = 0.88;
gBrickAo = 1.0;
gBrickHeight = 0.5;
gSpecAa = 0.0;
vec3 ap = abs(vObjP);
vec3 face = vec3(0.0);
if (ap.x >= ap.y && ap.x >= ap.z) face = vec3(sign(vObjP.x), 0.0, 0.0);
else if (ap.y >= ap.z) face = vec3(0.0, sign(vObjP.y), 0.0);
else face = vec3(0.0, 0.0, sign(vObjP.z));
vec3 weathered = diffuseColor.rgb;
vec3 clay = vec3(${CLAY_SALMON[0]}, ${CLAY_SALMON[1]}, ${CLAY_SALMON[2]});
vec3 mortar = vec3(${MORTAR_CRUST[0]}, ${MORTAR_CRUST[1]}, ${MORTAR_CRUST[2]});
vec3 mortarDeep = vec3(${MORTAR[0]}, ${MORTAR[1]}, ${MORTAR[2]});
vec3 soot = vec3(${SOOT[0]}, ${SOOT[1]}, ${SOOT[2]});
vec3 col = weathered;
if (abs(face.y) > 0.5) {
  float grain = 0.5 + 0.5 * sin(vSeed * 5.2);
  col = mix(mortarDeep, mortar, 0.55 + grain * 0.3);
} else if (abs(face.x) > 0.5) {
  float bit = face.x > 0.0 ? 1.0 : 2.0;
  float broken = step(0.5, mod(floor(vBroken / bit), 2.0));
  col = mix(weathered, clay, max(broken, 0.85));
} else {
  float bit = face.z > 0.0 ? 4.0 : 8.0;
  float broken = step(0.5, mod(floor(vBroken / bit), 2.0));
  col = mix(weathered, clay, broken);
}
if (vSoot > 0.04 && face.z > 0.5) {
  float luma = dot(col, vec3(0.2126, 0.7152, 0.0722));
  col = mix(col, mix(vec3(luma) * 0.18, soot, 0.7), clamp(vSoot, 0.0, 1.0));
}
float edge = min(min(ap.x, ap.y), ap.z);
col *= mix(0.72, 1.0, smoothstep(0.0, 0.08, edge));
float fp = length(fwidth(vObjP));
float n1 = brickNoiseBL(vObjP.xy * 3.4, 1.0, vSeed * 4.1, fp);
float n2 = brickNoiseBL(vObjP.zy * 7.0, 1.0, vSeed * 7.7, fp);
gBrickHeight = 0.5 + (n1 - 0.5) * 0.028 + (n2 - 0.5) * 0.010;
gBrickRough = clamp(mix(0.80, 0.94, brickHash11(vSeed * 3.3)) + (n2 - 0.5) * 0.06, 0.75, 0.98);
gBrickAo = mix(0.78, 1.0, smoothstep(0.0, 0.12, edge));
col *= mix(1.0, gBrickAo, 0.3);
diffuseColor.rgb = col;
`;

export function createDebrisMaterial(): DebrisMaterialHandles {
  const material = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    roughness: 0.88,
    metalness: 0.02,
    dithering: false,
  });
  material.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${DEBRIS_VERT}`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>\nvObjP = position;\nvSeed = fract(instanceA.x * 0.000983);\nvSoot = instanceA.y;\nvBroken = instanceA.z;`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${DEBRIS_FRAG}`)
      .replace('vec4 diffuseColor = vec4( diffuse, opacity );', `vec4 diffuseColor = vec4( diffuse, opacity );\n${DEBRIS_BODY}`)
      .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>\n${ROUGH_INJECT}`)
      .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>\n${NORMAL_INJECT}`)
      .replace('#include <aomap_fragment>', `#include <aomap_fragment>\n${AO_INJECT}`);
  };
  material.customProgramCacheKey = () => 'damage-debris-p4-matpass-fix1';
  return {
    material,
    dispose() {
      material.dispose();
    },
  };
}

const SHARD_VERT = /* glsl */ `
attribute float aShardSeed;
attribute vec3 surfaceRestPosition;
varying vec3 vShardP;
varying vec3 vShardRest;
flat varying float vShardSeed;
`;

const SHARD_FRAG = /* glsl */ `
varying vec3 vShardP;
varying vec3 vShardRest;
flat varying float vShardSeed;
${BRICK_NOISE_GLSL}
`;

const SHARD_BODY = /* glsl */ `
gBrickRough = 0.86;
gBrickAo = 1.0;
gBrickHeight = 0.5;
gSpecAa = 0.0;
vec3 rp = dot(vShardRest, vShardRest) > 1.0e-10 ? vShardRest : vShardP;
vec3 lp = vShardP;
vec3 ap = abs(lp);
vec2 uv = rp.xy;
if (ap.x >= ap.y && ap.x >= ap.z) uv = rp.zy;
else if (ap.y >= ap.z) uv = rp.xz;
float fp = length(fwidth(rp));
float s0 = vShardSeed;
float s1 = fract(vShardSeed * 7.132 + 0.173);
float nBroad = brickNoiseBL(uv, 16.0, s0 * 19.0, fp);
float grain = brickNoiseBL(uv, 28.0, s1 * 11.0, fp);
float und = sin(uv.x * 22.0 + s0 * 6.28318) * sin(uv.y * 17.0 + s1 * 4.188);
float undFade = brickOctaveFade(0.045, fp);
gBrickHeight = 0.5 + (nBroad - 0.5) * 0.034 + (grain - 0.5) * 0.016 + und * 0.010 * undFade;
gBrickRough = clamp(mix(0.76, 0.93, s0) + (grain - 0.5) * 0.10 + (nBroad - 0.5) * 0.05, 0.74, 0.98);
float edge = min(min(ap.x, ap.y), ap.z);
gBrickAo = mix(0.78, 1.0, smoothstep(0.0, 0.04, edge));
diffuseColor.rgb *= 1.0 + (grain - 0.5) * 0.18 + (nBroad - 0.5) * 0.08;
diffuseColor.rgb *= mix(1.0, gBrickAo, 0.22);
`;

/** Per-mesh tint multiplier + seed for the shared shard exterior material. */
export function stampShardVariance(
  geometry: THREE.BufferGeometry,
  id: number,
  cx: number,
  cy: number,
  cz: number,
): number {
  const pos = geometry.getAttribute('position');
  const n = pos?.count ?? 0;
  const seed = hash01(id, cx, cy, cz);
  const mulR = 0.90 + hash01(id, 3) * 0.20;
  const mulG = 0.92 + hash01(id, 8) * 0.14;
  const mulB = 0.94 + hash01(id, 13) * 0.10;
  const col = new Float32Array(n * 3);
  const seeds = new Float32Array(n);
  for (let i = 0; i < n; i += 1) {
    col[i * 3] = mulR;
    col[i * 3 + 1] = mulG;
    col[i * 3 + 2] = mulB;
    seeds[i] = seed;
  }
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  geometry.setAttribute('aShardSeed', new THREE.Float32BufferAttribute(seeds, 1));
  return seed;
}

/** Cheap exterior look for siege hull fragments so shards match the wall. */
export function createShardExteriorMaterial(color: THREE.Color): THREE.MeshStandardMaterial {
  const material = new THREE.MeshStandardMaterial({
    color,
    roughness: 0.86,
    metalness: 0.02,
    envMapIntensity: 0.18,
    vertexColors: true,
    dithering: false,
  });
  material.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${SHARD_VERT}`)
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>\nvShardP = position;\nvShardRest = ${SURFACE_REST_POSITION_ATTRIBUTE};\nvShardSeed = aShardSeed;`,
      );
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${SHARD_FRAG}`)
      .replace('vec4 diffuseColor = vec4( diffuse, opacity );', `vec4 diffuseColor = vec4( diffuse, opacity );\n${SHARD_BODY}`)
      .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>\n${ROUGH_INJECT}`)
      .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>\n${NORMAL_INJECT}`)
      .replace('#include <aomap_fragment>', `#include <aomap_fragment>\n${AO_INJECT}`);
  };
  material.customProgramCacheKey = () => 'damage-shard-p4-matpass-fix1';
  return material;
}
